import { action, thunk } from 'easy-peasy';
import {
  TableRows,
  TableRowsWithoutCompensation,
} from '@app/pages/stepper/showcase/constants/table-rows';
import { getApplicationsSortedByState } from '@app/pages/stepper/showcase/helpers/sort-candidates';
import {
  COMPENSATION_VISIBILITY_KEY,
  COMPENSATION_VISIBILITY_VALUES,
} from '@app/constants/common';

const reorderList = (list, startPositionId, landingPositionId) => {
  const startPosition = list.findIndex(
    listItem => listItem.id === startPositionId,
  );
  const landingPosition = list.findIndex(
    listItem => listItem.id === landingPositionId,
  );
  const [movedItem] = list.splice(startPosition, 1);

  list.splice(landingPosition, 0, movedItem);
  return list;
};

const hideListItem = (list, itemId) => {
  return list.map(listItem => {
    if (itemId === listItem.id) {
      listItem.isVisible = false;
    }

    return listItem;
  });
};

const appendNewItemsToList = (outputList, item) => {
  const isAlreadyInOutputList = outputList.find(
    outputListItem => outputListItem.id === item.id,
  );
  if (!!isAlreadyInOutputList) return outputList;

  return [
    ...outputList,
    {
      id: item.id,
      isVisible: true,
    },
  ];
};

const getIsCompensationVisible = (
  localCompensationVisibilityStatus,
  candidates,
) => {
  if (localCompensationVisibilityStatus) {
    return (
      localCompensationVisibilityStatus ===
      COMPENSATION_VISIBILITY_VALUES.VISIBLE
    );
  }

  return candidates.findIndex(c => c.compensationPackage !== null) > -1;
};

const showcaseTableModel = {
  rows: [],
  columns: [],
  areDiscardedVisible: false,
  areInterestedButVisible: false,
  isEditedByUser: false,
  lastSaveDate: null,
  isConfigurationLoaded: false,
  isLoading: false,
  showcaseConfiguration: {},
  compensationVisibilityStatus: window.localStorage.getItem(
    COMPENSATION_VISIBILITY_KEY,
  ),
  isCompensationVisible: true,

  initTable: thunk((actions, payload, { getStoreState }) => {
    const {
      candidates: { candidates },
      showcaseTable: { showcaseConfiguration, compensationVisibilityStatus },
    } = getStoreState();

    if (!candidates) {
      actions.setIsConfigurationLoaded(true);
      return;
    }

    const isCompensationVisible = getIsCompensationVisible(
      compensationVisibilityStatus,
      candidates.results,
    );

    actions.setIsCompensationVisible(isCompensationVisible);

    actions.setRows(showcaseConfiguration?.attributes);
    actions.setColumns({
      candidates: candidates.results,
      showcaseConfiguration: showcaseConfiguration?.candidates,
    });
    actions.setInterestedButVisibility(
      showcaseConfiguration?.showCandidatesWithConcerns || false,
    );
    actions.setDiscardedVisibility(
      showcaseConfiguration?.showDiscardedCandidates || false,
    );

    actions.setIsEditedByUser(false);
    actions.setIsConfigurationLoaded(true);
  }),

  saveTableStatus: thunk(
    async (actions, payload, { getState, getStoreState, getStoreActions }) => {
      const { job } = getStoreState();
      const jobId = job.data?.id;
      const {
        session: { authFetch },
        job: { showcase },
      } = getStoreActions();
      const { rows, columns, areDiscardedVisible, areInterestedButVisible } =
        getState();

      const requestPayload = {
        candidates: columns.map(column => ({
          id: column.id,
          isVisible: column.isVisible,
        })),
        attributes: rows.map(row => ({
          name: row.id,
          isVisible: row.isVisible,
        })),
        showCandidatesWithConcerns: areInterestedButVisible,
        showDiscardedCandidates: areDiscardedVisible,
      };

      try {
        const res = await showcase.putShowcase({
          jobId,
          ...requestPayload,
        });
        actions.setShowcaseConfiguration(res.showcaseConfiguration);
        actions.setLastSaveDate(new Date());
      } catch (error) {
        console.error(error);
      }
    },
  ),

  resetTable: thunk(
    async (actions, payload, { getStoreState, getStoreActions }) => {
      const { candidates, job } = getStoreState();
      const _actions = getStoreActions();
      const jobId = job.data?.id;
      const {
        session: { authFetch },
      } = getStoreActions();

      actions.setIsEditedByUser(false);
      actions.clearFilters(candidates.candidates.results);

      try {
        await _actions.job.showcase.deleteShowcase({ jobId });
        actions.setShowcaseConfiguration(null);
        actions.setLastSaveDate(null);
      } catch (error) {
        console.error(error);
      }
    },
  ),

  setRows: action((state, showcaseConfiguration = []) => {
    const rows = state.isCompensationVisible
      ? TableRows
      : TableRowsWithoutCompensation;

    const showcaseConfigurationFiltered = showcaseConfiguration.filter(row =>
      rows.some(tableRow => tableRow.id === row.name),
    );

    const convertedConfiguration = showcaseConfigurationFiltered.map(row => ({
      ...row,
      id: row.name,
    }));

    const configurationWithNewAttributes = rows.reduce(
      appendNewItemsToList,
      convertedConfiguration,
    );

    state.rows = configurationWithNewAttributes.map(attribute => {
      const tableRowConfiguration = rows.find(
        tableRow => tableRow.id === attribute.id,
      );

      return {
        ...attribute,
        hasVisibilityAction: tableRowConfiguration?.hasVisibilityAction,
      };
    });
  }),

  setColumns: action((state, { candidates, showcaseConfiguration = [] }) => {
    const sortedApplications = getApplicationsSortedByState(candidates);
    const configurationWithNewApplications = sortedApplications.reduce(
      appendNewItemsToList,
      showcaseConfiguration,
    );

    state.columns = configurationWithNewApplications.reduce(
      (outputColumns, column) => {
        const currentApplication = sortedApplications.find(
          application => application.id === column.id,
        );
        if (!currentApplication) return outputColumns;

        return [
          ...outputColumns,
          {
            candidateId: currentApplication.id,
            id: String(column.id),
            isVisible: column.isVisible,
            isDiscarded: !!currentApplication.discardedAt,
            isInterestedBut: currentApplication.concerns !== null,
          },
        ];
      },
      [],
    );
  }),

  setDiscardedVisibility: action((state, areDiscardedVisible) => {
    state.isEditedByUser = true;
    state.areDiscardedVisible = areDiscardedVisible;
  }),

  setInterestedButVisibility: action((state, areInterestedButVisible) => {
    state.isEditedByUser = true;
    state.areInterestedButVisible = areInterestedButVisible;
  }),

  clearFilters: action((state, candidates) => {
    const rows = state.isCompensationVisible
      ? TableRows
      : TableRowsWithoutCompensation;

    const sortedApplications = getApplicationsSortedByState(candidates);

    state.areDiscardedVisible = false;
    state.areInterestedButVisible = false;

    state.columns = sortedApplications.map(application => ({
      id: String(application.id),
      isVisible: true,
      isDiscarded: !!application.discardedAt,
      isInterestedBut: application.concerns !== null,
    }));

    state.rows = rows.map(row => ({
      ...row,
      isVisible: true,
    }));
  }),

  setRowsOrder: action((state, { startPositionId, landingPositionId }) => {
    const newRowsOrder = [...state.rows];
    state.isEditedByUser = true;
    state.rows = reorderList(newRowsOrder, startPositionId, landingPositionId);
  }),

  setColumnsOrder: action((state, { startPositionId, landingPositionId }) => {
    const newColumnsOrder = [...state.columns];
    state.isEditedByUser = true;
    state.columns = reorderList(
      newColumnsOrder,
      startPositionId,
      landingPositionId,
    );
  }),

  hideColumn: action((state, columnId) => {
    state.isEditedByUser = true;
    state.columns = hideListItem([...state.columns], columnId);
  }),

  hideRow: action((state, rowName) => {
    state.isEditedByUser = true;
    state.rows = hideListItem([...state.rows], rowName);
  }),

  toggleCompensationVisibilityStatus: thunk(
    (actions, payload, { getStoreState }) => {
      const { compensationVisibility } = payload;

      actions.setIsConfigurationLoaded(false);
      actions.setCompensationVisibilityStatus(compensationVisibility);

      actions.initTable();
    },
  ),

  setIsEditedByUser: action((state, isEditedByUser) => {
    state.isEditedByUser = isEditedByUser;
  }),

  setLastSaveDate: action((state, lastSaveDate) => {
    state.lastSaveDate = lastSaveDate;
  }),

  setIsConfigurationLoaded: action((state, isConfigurationLoaded) => {
    state.isConfigurationLoaded = isConfigurationLoaded;
  }),

  setIsLoading: action((state, isLoading) => {
    state.isLoading = isLoading;
  }),

  setShowcaseConfiguration: action((state, showcaseConfiguration) => {
    state.showcaseConfiguration = showcaseConfiguration;
  }),

  setIsCompensationVisible: action((state, isCompensationVisible) => {
    state.isCompensationVisible = isCompensationVisible;
  }),

  setCompensationVisibilityStatus: action(
    (state, compensationVisibilityStatus) => {
      state.compensationVisibilityStatus = compensationVisibilityStatus;
    },
  ),
};

export const selectorIsCompensationVisible = state =>
  state.showcaseTable.isCompensationVisible;

export default showcaseTableModel;
